import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { searchTenants } from "../api/usersAPI";
import { useTheme } from "../context/ThemeContext";
import { Card, PageHeading } from "../components/ui";

/**
 * TenantSearchPage is the PRE-TENANT club finder: it renders before any
 * tenant has been chosen, which means `applyCSSVariables()` has not run and
 * `--color-primary` (and its derived vars) are unset. Any primitive that
 * paints with those vars — `Button variant="primary"`, and the brand focus
 * ring baked into `Input` — would resolve to an invalid color here, so the
 * search field and the CTA below stay hand-rolled in Club Central's own
 * product orange. Everything that is theme-only (PageHeading, Card) uses the
 * primitives. See the batch notes for the variant this is asking for.
 */
const TenantSearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const { isDarkMode } = useTheme();

  // Live search as user types (debounced, min 3 chars)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.trim().length < 3) {
      if (searchQuery.trim().length === 0) {
        setSearchResults([]);
        setHasSearched(false);
      }
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setHasSearched(true);

      try {
        const results = await searchTenants(searchQuery);
        setSearchResults(results || []);
      } catch (error) {
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const results = await searchTenants(searchQuery);
      setSearchResults(results || []);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = (tenant: any) => {
    const tenantPath = tenant.tenant_id || tenant.id;
    navigate(`/${tenantPath}`);
  };

  const searchDisabled = loading || !searchQuery.trim();

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
      {/* Header */}
      <header className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-4xl mx-auto flex justify-center">
          <img
            src={isDarkMode ? "/club-central-marketing.png" : "/club-central-marketing-b.png"}
            alt="Club Central"
            className="h-20 sm:h-24 w-auto"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Heading */}
          <PageHeading
            title="Find Your Club"
            subtitle="Search by club name or location to get started"
            className="text-center"
          />

          {/* Search Box */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg
                    className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {/* Hand-rolled, and the original reason no longer applies:
                    <Input>'s focus ring var used to be unset on this pre-tenant
                    route, leaving no visible focus indicator. It is now the
                    platform --cc-focus with a :root value in index.css, so the
                    ring works.

                    It stays hand-rolled for a different reason: this page uses a
                    bespoke pre-tenant palette (slate borders, white field) and
                    <Input> hardcodes getInputStyles(isDarkMode). Tailwind resolves
                    same-specificity utilities by stylesheet SOURCE order, not
                    className order, so passing colours through className is not
                    guaranteed to win. Converting needs a prop that omits the
                    built-in styles, or a look at the rendered page. */}
                <input
                  aria-label="Enter club name or location"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter club name or location..."
                  className={`w-full pl-11 pr-4 py-3 rounded-lg text-base transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    isDarkMode
                      ? 'bg-gray-800 border border-gray-700 text-white placeholder-gray-500'
                      : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400'
                  }`}
                />
              </div>
              {/* Hand-rolled: Club Central product orange, not a tenant brand
                  color, so no Button variant fits. The JS hover handlers this
                  replaced are now plain `hover:` classes. */}
              <button
                type="submit"
                disabled={searchDisabled}
                className={`px-6 py-3 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 min-w-[120px] ${
                  searchDisabled
                    ? (isDarkMode ? 'bg-gray-700 cursor-not-allowed' : 'bg-slate-300 cursor-not-allowed')
                    : 'bg-[#FF6600] hover:bg-[#e55c00]'
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Searching</span>
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
          </form>

          {/* Search Results */}
          {hasSearched && (
            <div className="mt-6">
              {searchResults.length > 0 ? (
                <Card padding="none" shadow="sm" className="overflow-hidden">
                  <div className={`px-4 py-3 border-b ${
                    isDarkMode
                      ? 'bg-gray-750 border-gray-700'
                      : 'bg-slate-50 border-slate-200'
                  }`}>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                      {searchResults.length} club{searchResults.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                  <ul className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-slate-200'}`}>
                    {searchResults.map((tenant) => (
                      <li key={tenant.id}>
                        {/* A full-bleed list row, not a <Button> — no button
                            size/variant expresses this layout. */}
                        <button
                          onClick={() => handleTenantSelect(tenant)}
                          className={`w-full px-4 py-4 text-left transition-colors flex items-center justify-between group ${
                            isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 ${
                              isDarkMode ? 'bg-gray-700' : 'bg-slate-100'
                            }`}>
                              {tenant.logo ? (
                                <img
                                  src={tenant.logo}
                                  alt={`${tenant.name} logo`}
                                  className="w-full h-full object-contain p-1"
                                />
                              ) : (
                                <svg
                                  className={`w-6 h-6 ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                  />
                                </svg>
                              )}
                            </div>
                            <div>
                              <h3 className={`font-semibold group-hover:text-orange-500 transition-colors ${
                                isDarkMode ? 'text-white' : 'text-slate-900'
                              }`}>
                                {tenant.name}
                              </h3>
                              <div className="flex items-center gap-3 mt-1">
                                {tenant.tenant_id && (
                                  <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>
                                    {tenant.tenant_id}
                                  </span>
                                )}
                                {tenant.location && (
                                  <span className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>
                                    <svg
                                      className="w-3.5 h-3.5"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    {tenant.location}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 group-hover:text-orange-500 group-hover:translate-x-1 transition-all flex-shrink-0 ${
                              isDarkMode ? 'text-gray-600' : 'text-slate-400'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Card>
              ) : (
                <Card padding="lg" className="text-center">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    isDarkMode ? 'bg-gray-700' : 'bg-slate-100'
                  }`}>
                    <svg
                      className={`w-7 h-7 ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <h3 className={`font-semibold text-lg mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    No clubs found
                  </h3>
                  <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                    We couldn't find any clubs matching "{searchQuery}".
                    Check your spelling or try a different search term.
                  </p>
                </Card>
              )}
            </div>
          )}

          {/* Helper Text - shown before search */}
          {!hasSearched && (
            <div className={`mt-4 flex items-start gap-3 p-4 rounded-lg ${
              isDarkMode
                ? 'bg-orange-900/20 border border-orange-800/30'
                : 'bg-orange-50 border border-orange-100'
            }`}>
              <svg
                className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className={`text-sm ${isDarkMode ? 'text-orange-200' : 'text-orange-800'}`}>
                <span className="font-medium">Looking for your club?</span>{" "}
                Enter the club name or location above. If you're not sure,
                contact your club administrator for the correct details.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`max-w-4xl mx-auto border-t pt-8 ${isDarkMode ? 'border-gray-800' : 'border-slate-200'}`}>
          <p className={`text-center text-sm ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>
            © {new Date().getFullYear()} Club Central. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TenantSearchPage;
